import React from 'react';
import { motion } from 'framer-motion';

import FadeIn from '../../../components/animations/FadeIn';
import CountUp from '../../../components/animations/CountUp';
import { useGoogleStats } from '../../../hooks/useGoogleStats';

// Trust Bridge — thin gradient band between the dark Zero Section and the
// white Reviews / SocialProof section. Serves two purposes:
//   1. Visual transition: the section's background gradient is anchored at
//      its top to the Zero Section's --ink (#0a1628) and at its bottom to
//      pure white — so the two surrounding sections butt against it with
//      no visible seam.
//   2. Three high-impact stats (verified-reviews count, Google rating,
//      $0-down punchline). The "2 → 13 cities by end of 2026" stat was
//      removed 2026-05-24 — it was a future projection sitting among
//      proven facts.
//
// First two numeric stats (verified-reviews count, 5.0) count up from 0
// via CountUp; the "$0" punchline scale-ins dramatically instead.
//
// LIVE DATA: the verified-reviews count is now sourced from
// `useGoogleStats()` — same Supabase aggregate (`fetchPlaceStats`) that
// powers GoogleReviewsBadge, RecentInstallsSection, and the
// SocialProofSection tier-A stats. The hook's module-level cache means
// ONE fetch per page load shared across all four consumers, so they
// can never drift apart.

const SCALE_IN_TRANSITION = {
  duration: 0.8,
  delay: 0.4,
  ease: [0.16, 1, 0.3, 1],
};

// Last-known Google aggregate. Shown for the brief moment before
// useGoogleStats resolves on first page load and as a safety net if
// the Supabase fetch fails. Matches the fallback in
// RecentInstallsSection and SocialProofSection.
const FALLBACK_TOTAL_REVIEWS = 32;

const TrustBridge = () => {
  // Live verified-reviews count from the shared Supabase aggregate.
  // Same hook used by SocialProofSection, RecentInstallsSection, and
  // GoogleReviewsBadge — the module-level cache means a single fetch
  // is shared across all four consumers (zero extra network cost).
  const { stats } = useGoogleStats();
  const totalReviews = Math.floor(stats?.totalReviews ?? FALLBACK_TOTAL_REVIEWS);

  return (
  <section className="trust-bridge">
    <div className="container">
      <div className="trust-bridge-stats">
        <FadeIn className="bridge-stat" delay={0.08}>
          <motion.div
            className="bridge-stat-num"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <CountUp to={totalReviews} />
          </motion.div>
          <div className="bridge-stat-glow" />
          <div className="bridge-stat-label">Verified reviews</div>
        </FadeIn>

        <FadeIn className="bridge-stat" delay={0.16}>
          <motion.div
            className="bridge-stat-num"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <CountUp to={5} decimals={1} />
          </motion.div>
          <div className="bridge-stat-glow" />
          <div className="bridge-stat-label">Google rating</div>
        </FadeIn>

        {/* "$0" punchline — scale-in for emphasis. (Previously sat in
            the 4th slot alongside a "2 → 13 cities by end of 2026"
            projection; that projection was removed 2026-05-24 — future
            forecast vs. proven facts — and the layout was rebalanced
            to 3 evenly-distributed stats.) */}
        <FadeIn className="bridge-stat" delay={0.24}>
          <motion.div
            className="bridge-stat-num"
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.05 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={SCALE_IN_TRANSITION}
          >
            $0
          </motion.div>
          <div className="bridge-stat-glow" />
          <div className="bridge-stat-label">Down at signing</div>
        </FadeIn>
      </div>
    </div>
  </section>
  );
};

export default TrustBridge;
